<script setup lang="ts">
import { useResizeObserver, useWindowSize } from '@vueuse/core'
import { computed, ref, useSlots, type PropType } from 'vue'
import { useMediaQuery } from '../../composables/useMediaQuery'
import { useExposeOverlay, useVModelOverlay } from '../../composables/useOverlay'
import OenUiCard from '../OenUiCard/OenUiCard.vue'
import OenUiOverlay from '../OenUiOverlay/OenUiOverlay.vue'

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: '',
  },
  dismissible: {
    type: Boolean,
    required: false,
    default: false,
  },
  /** Target container - CSS selector or DOM node */
  to: {
    type: [String, Object] as PropType<string | HTMLElement>,
    required: false,
    default: 'body',
  },
  modelValue: {
    type: Boolean,
    required: false,
    default: false,
  },
  focusTrap: {
    type: Boolean,
    required: false,
    default: false,
  },
  wide: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  /** Transition name of the content element */
  transitionType: {
    type: String,
    required: false,
    default: undefined,
  },
})

const emits = defineEmits(["update:modelValue"])

const slots = useSlots()

const mq = useMediaQuery()
const wrapper = ref<HTMLElement>()
const { height: windowHeight } = useWindowSize()
const cardHeight = ref<number | undefined>(0)

useResizeObserver(
  wrapper,
  () => {
    cardHeight.value = wrapper?.value?.offsetHeight
  },
  {
    box: 'border-box',
  }
)

const classes = computed(() => {
  return {
    'oen-ui-modal--xs': mq.current === 'xs',
    'oen-ui-modal--scrolling': cardHeight?.value ? cardHeight.value > windowHeight.value : 0,
    'oen-ui-modal--wide': !!props.wide,
  }
})

// Re-expose OenUiOverlay properties/methods
const { overlayRef, reveal, cancel, confirm, isRevealed, onCancel, onConfirm } = useExposeOverlay()

defineExpose({
  reveal,
  confirm,
  cancel,
  isRevealed,
  onCancel,
  onConfirm,
})

useVModelOverlay({
  props,
  emits,
  cancel,
  confirm,
  onCancel,
  onConfirm,
  reveal,
  isRevealed,
})

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <OenUiOverlay
    ref="overlayRef"
    :dismissible="dismissible"
    :focus-trap="focusTrap"
    :to="to"
    class="oen-ui-modal"
    :class="classes"
    :transition-type="transitionType"
    v-bind="$attrs"
  >
    <template #default="{ confirm, cancel }">
      <div ref="wrapper" class="oen-ui-modal-wrapper">
        <OenUiCard role="dialog" :title="title" modal>
          <template v-if="slots.header" #header>
            <slot name="header" :cancel="cancel" />
          </template>

          <template v-if="slots.figureTitle" #figureTitle>
            <slot name="figureTitle" />
          </template>

          <slot :confirm="confirm" :cancel="cancel" />

          <template v-if="slots.footer" #footer>
            <slot name="footer" :confirm="confirm" :cancel="cancel" />
          </template>
        </OenUiCard>
      </div>
    </template>
  </OenUiOverlay>
</template>

<style scoped>
.oen-ui-modal {
  .oen-ui-modal-wrapper {
    @apply h-full;

    @screen sm {
      @apply h-auto;
    }
  }

  :deep(.oen-ui-overlay-content) {
    @apply w-full;
    @apply h-full;

    @screen sm {
      @apply max-w-md;
      @apply h-auto;
    }
  }

  &.oen-ui-modal--scrolling {
    @screen sm {
      @apply py-16;
    }
  }

  &.oen-ui-modal--xs {
    :deep(.oen-ui-overlay-content) {
      @apply h-full;
      @apply w-full;
      @apply m-0;
      @apply p-0;
    }
  }

  &.oen-ui-modal--wide {
    :deep(.oen-ui-overlay-content) {
      @screen sm {
        @apply max-w-3xl;
      }
    }

    :deep(.oen-ui-card) {
      @apply max-w-3xl;
      @apply h-full;
      @apply flex;
    }
  }
}
</style>
